import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppSettings } from './config/app-settings.js';
import { slideToggle } from './composables/slideToggle.js';
import { useLocation, useNavigate } from 'react-router-dom';
import Content from './components/content/content.jsx';
import TokenExpiryChecker from './components/CustomComponents/TokenExpiryChecker/TokenExpiryChecker.jsx';
import useUserDataRefresh from './hooks/useUserDataRefresh.js';
import useGlobalThemeColors from './hooks/useGlobalThemeColors.js';
import { RestaurantProvider, useRestaurant } from './contexts/RestaurantContext.jsx';
import { setCurrentRestaurant } from './app/cartSlice.js';
import {
	initializeSettings,
	setAppTheme,
	setAppDarkMode,
	setAppGradientEnabled,
	setAppHeaderFixed,
	setAppHeaderInverse,
} from './app/appSettingsSlice.js';
import {
	restoreCustomerFromStorage,
	customerLogout,
	updateCustomerToken,
	selectCustomerToken,
	selectCustomerRefreshToken,
	selectIsCustomerLoggedIn,
} from './app/customerAuthSlice.js';
import { isTokenExpired, getTimeUntilExpiry } from './utils/tokenUtils.js';
import { tryRefreshCustomerToken } from './config/axiosApi/axiosApi.jsx';

function AppContent() {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { restaurantSlug } = useRestaurant();
	const customerToken = useSelector(selectCustomerToken);
	const customerRefreshToken = useSelector(selectCustomerRefreshToken);
	const isCustomerLoggedIn = useSelector(selectIsCustomerLoggedIn);

	// Get app settings from Redux (restaurant-scoped)
	const appSettings = useSelector((state) => {
		if (!restaurantSlug) {
			return {
				appTheme: 'teal',
				appDarkMode: false,
				appGradientEnabled: true,
				appHeaderFixed: true,
				appHeaderInverse: false,
			};
		}
		return state.appSettings.settings[restaurantSlug] || {
			appTheme: 'teal',
			appDarkMode: false,
			appGradientEnabled: true,
			appHeaderFixed: true,
			appHeaderInverse: false,
		};
	});
	const {
		appTheme,
		appDarkMode,
		appGradientEnabled,
		appHeaderFixed,
		appHeaderInverse,
	} = appSettings;
	const isLightModeLocked = true;

	// Initialize user data auto-refresh
	useUserDataRefresh();

	useGlobalThemeColors();

	// Restore customer auth from localStorage on app load
	useEffect(() => {
		dispatch(restoreCustomerFromStorage({ restaurantSlug }));
	}, [dispatch, restaurantSlug]);

	const redirectIfOnAccountPage = () => {
		const isCustomerOrderPage =
			location.pathname.includes('/my-orders') ||
			location.pathname.includes('/track-order') ||
			location.pathname.includes('/order-details') ||
			location.pathname.includes('/notifications');
		if (isCustomerOrderPage) {
			navigate(restaurantSlug ? `/${restaurantSlug}/home` : '/', { replace: true });
		}
	};

	// If the access token is expired, refresh it the same way BackOffice does.
	useEffect(() => {
		if (!isCustomerLoggedIn || !restaurantSlug) return;
		if (customerToken && !isTokenExpired(customerToken)) return;

		const presented = customerRefreshToken || customerToken;
		if (!presented) {
			dispatch(customerLogout({ restaurantSlug }));
			redirectIfOnAccountPage();
			return undefined;
		}

		let cancelled = false;
		(async () => {
			const refreshed = await tryRefreshCustomerToken(restaurantSlug);
			if (cancelled) return;
			if (refreshed?.customerToken) {
				dispatch(updateCustomerToken({
					token: refreshed.customerToken,
					refreshToken: refreshed.customerRefreshToken,
					customer: refreshed.customerUser,
					restaurantSlug,
				}));
				return;
			}
			dispatch(customerLogout({ restaurantSlug }));
			redirectIfOnAccountPage();
		})();

		return () => {
			cancelled = true;
		};
	}, [customerToken, customerRefreshToken, isCustomerLoggedIn, dispatch, location.pathname, navigate, restaurantSlug]);

	// Refresh ~5 minutes before expiry so checkout never hits a dead token.
	useEffect(() => {
		if (!isCustomerLoggedIn || !restaurantSlug || !customerToken) return;
		if (isTokenExpired(customerToken)) return;

		const timeUntilExpiry = getTimeUntilExpiry(customerToken);
		const refreshThreshold = 300;
		const minDelay = 30000;
		const delayMs =
			timeUntilExpiry <= refreshThreshold
				? minDelay
				: Math.max((timeUntilExpiry - refreshThreshold) * 1000, minDelay);

		const timer = window.setTimeout(async () => {
			const refreshed = await tryRefreshCustomerToken(restaurantSlug);
			if (refreshed?.customerToken) {
				dispatch(updateCustomerToken({
					token: refreshed.customerToken,
					refreshToken: refreshed.customerRefreshToken,
					customer: refreshed.customerUser,
					restaurantSlug,
				}));
			}
		}, delayMs);

		return () => window.clearTimeout(timer);
	}, [customerToken, customerRefreshToken, isCustomerLoggedIn, restaurantSlug, dispatch]);

	// Keep Redux in sync when axios refreshes the token.
	useEffect(() => {
		const handleCustomerTokenRefreshed = (event) => {
			const { customerToken: token, customerRefreshToken: refreshToken, customerUser, restaurantSlug: slug } =
				event.detail || {};
			if (!token) return;
			dispatch(updateCustomerToken({
				token,
				refreshToken,
				customer: customerUser,
				restaurantSlug: slug || restaurantSlug,
			}));
		};

		window.addEventListener('customerTokenRefreshed', handleCustomerTokenRefreshed);
		return () => window.removeEventListener('customerTokenRefreshed', handleCustomerTokenRefreshed);
	}, [dispatch, restaurantSlug]);

	// Handle forced customer logout events from axios interceptors
	useEffect(() => {
		const handleForceCustomerLogout = () => {
			dispatch(customerLogout({ restaurantSlug }));
			const isCustomerOrderPage =
				location.pathname.includes('/my-orders') ||
				location.pathname.includes('/track-order') ||
				location.pathname.includes('/order-details') ||
				location.pathname.includes('/notifications');

			if (isCustomerOrderPage) {
				navigate(restaurantSlug ? `/${restaurantSlug}/home` : '/', { replace: true });
			}
		};

		window.addEventListener('forceCustomerLogout', handleForceCustomerLogout);
		return () => window.removeEventListener('forceCustomerLogout', handleForceCustomerLogout);
	}, [dispatch, location.pathname, navigate, restaurantSlug]);

	// Initialize cart and settings for current restaurant
	useEffect(() => {
		if (restaurantSlug) {
			dispatch(setCurrentRestaurant({ restaurantSlug }));
			// Initialize app settings for this restaurant
			dispatch(initializeSettings({ restaurantSlug }));
		}
	}, [restaurantSlug, dispatch]);

	// Global ResizeObserver error handler
	useEffect(() => {
		// Override ResizeObserver globally to suppress the error
		const OriginalResizeObserver = window.ResizeObserver;
		window.ResizeObserver = class extends OriginalResizeObserver {
			constructor(callback) {
				super((entries, observer) => {
					try {
						callback(entries, observer);
					} catch (error) {
						if (error.message && error.message.includes('ResizeObserver')) {
							// Suppress ResizeObserver errors
							return;
						}
						throw error;
					}
				});
			}
		};

		const handleResizeObserverError = (e) => {
			if (
				e.message ===
				"ResizeObserver loop completed with undelivered notifications."
			) {
				e.stopImmediatePropagation();
				e.preventDefault();
				return false;
			}
		};

		const handleUnhandledRejection = (e) => {
			if (
				e.reason &&
				e.reason.message &&
				e.reason.message.includes("ResizeObserver")
			) {
				e.preventDefault();
				return false;
			}
		};

		// Add multiple event listeners to catch the error at different levels
		window.addEventListener("error", handleResizeObserverError, true);
		window.addEventListener("unhandledrejection", handleUnhandledRejection, true);

		// Also override console.error to suppress ResizeObserver errors
		const originalConsoleError = console.error;
		console.error = (...args) => {
			if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
				return; // Suppress ResizeObserver console errors
			}
			originalConsoleError.apply(console, args);
		};

		return () => {
			// Restore original ResizeObserver
			window.ResizeObserver = OriginalResizeObserver;
			window.removeEventListener("error", handleResizeObserverError, true);
			window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
			console.error = originalConsoleError;
		};
	}, []);

	// Local state for UI-specific settings not in Redux
	const [appHeaderNone, setAppHeaderNone] = useState(true);
	const [appHeaderMegaMenu, setAppHeaderMegaMenu] = useState(false);
	const [appHeaderLanguageBar, setAppHeaderLanguageBar] = useState(false);
	const [hasScroll, setHasScroll] = useState(false);
	const [appContentNone, setAppContentNone] = useState(false);
	const [appContentClass, setAppContentClass] = useState('');
	const [appContentFullHeight, setAppContentFullHeight] = useState(false);
	const [appTopMenu, setAppTopMenu] = useState(false);
	const [appTopMenuMobileToggled] = useState(false);

	// Initialize theme and dark mode from Redux state
	useEffect(() => {
		// Set theme
		if (appTheme) {
			const newTheme = 'theme-' + appTheme;
			for (var x = 0; x < document.body.classList.length; x++) {
				if (
					document.body.classList[x].indexOf('theme-') > -1 &&
					document.body.classList[x] !== newTheme
				) {
					document.body.classList.remove(document.body.classList[x]);
				}
			}
			document.body.classList.add(newTheme);
		}

		// Force light mode regardless of saved/user/device preferences
		document.querySelector('html').setAttribute('data-bs-theme', 'light');
	}, [appTheme, appDarkMode]);

	// Function to conditionally render components
	const renderComponents = () => {
		return (
			<>
				{!appContentNone && <Content />}
				<TokenExpiryChecker />
			</>
		);
	};

	const handleSetAppHeaderNone = (value) => {
		setAppHeaderNone(value);
	};

	const handleSetAppHeaderInverse = (value) => {
		if (restaurantSlug) {
			dispatch(setAppHeaderInverse({ restaurantSlug, inverse: value }));
		}
	};

	const handleSetAppHeaderLanguageBar = (value) => {
		setAppHeaderLanguageBar(value);
	};

	const handleSetAppHeaderMegaMenu = (value) => {
		setAppHeaderMegaMenu(value);
	};

	const handleSetAppHeaderFixed = (value) => {
		if (restaurantSlug) {
			dispatch(setAppHeaderFixed({ restaurantSlug, fixed: value }));
		}
	};

	const handleSetAppContentNone = (value) => {
		setAppContentNone(value);
	};

	const handleSetAppContentClass = (value) => {
		setAppContentClass(value);
	};

	const handleSetAppContentFullHeight = (value) => {
		setAppContentFullHeight(value);
	};

	const handleSetAppTopMenu = (value) => {
		setAppTopMenu(value);
	};

	const toggleAppTopMenuMobile = (e) => {
		e.preventDefault();
		slideToggle(document.querySelector('.app-top-menu'));
	};

	const handleSetAppBoxedLayout = (value) => {
		if (value === true) {
			document.body.classList.add('boxed-layout');
		} else {
			document.body.classList.remove('boxed-layout');
		}
	};

	const handleSetAppDarkMode = () => {
		// Keep bootstrap in light theme permanently.
		document.querySelector('html').setAttribute('data-bs-theme', 'light');
		if (restaurantSlug) {
			dispatch(setAppDarkMode({ restaurantSlug, darkMode: false }));
		}
		document.dispatchEvent(new Event('theme-reload'));
	};

	const handleSetAppGradientEnabled = (value) => {
		if (restaurantSlug) {
			dispatch(setAppGradientEnabled({ restaurantSlug, enabled: value }));
		}
	};

	const handleSetAppTheme = (value) => {
		var newTheme = 'theme-' + value;
		for (var x = 0; x < document.body.classList.length; x++) {
			if (
				document.body.classList[x].indexOf('theme-') > -1 &&
				document.body.classList[x] !== newTheme
			) {
				document.body.classList.remove(document.body.classList[x]);
			}
		}
		document.body.classList.add(newTheme);

		if (restaurantSlug) {
			dispatch(setAppTheme({ restaurantSlug, theme: value }));
		}
		document.dispatchEvent(new Event('theme-reload'));
	};

	useEffect(() => {
		handleSetAppTheme(appTheme);
		// Ensure the app always stays in light mode.
		handleSetAppDarkMode(false);

		const handleScroll = () => {
			if (window.scrollY > 0) {
				setHasScroll(true);
			} else {
				setHasScroll(false);
			}
			var elm = document.getElementsByClassName('nvtooltip');
			for (var i = 0; i < elm.length; i++) {
				elm[i].classList.add('d-none');
			}
		};

		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [appTheme, appDarkMode]);

	return (
		<AppSettings.Provider
			value={{
				appTheme,
				appDarkMode: isLightModeLocked ? false : appDarkMode,
				appGradientEnabled,
				appHeaderNone,
				appHeaderFixed,
				appHeaderInverse,
				appHeaderMegaMenu,
				appHeaderLanguageBar,
				hasScroll,
				handleSetAppHeaderNone,
				handleSetAppHeaderInverse,
				handleSetAppHeaderLanguageBar,
				handleSetAppHeaderMegaMenu,
				handleSetAppHeaderFixed,
				appContentNone,
				appContentClass,
				appContentFullHeight,
				handleSetAppContentNone,
				handleSetAppContentClass,
				handleSetAppContentFullHeight,
				appTopMenu,
				appTopMenuMobileToggled,
				toggleAppTopMenuMobile,
				handleSetAppTopMenu,
				handleSetAppBoxedLayout,
				handleSetAppDarkMode,
				handleSetAppGradientEnabled,
				handleSetAppTheme,
			}}
		>
			<div
				className={
					'app ' +
					(appGradientEnabled ? 'app-gradient-enabled ' : '') +
					(appHeaderNone ? 'app-without-header ' : '') +
					(appHeaderFixed && !appHeaderNone ? 'app-header-fixed ' : '') +
					'app-without-sidebar ' +
					(appTopMenu ? 'app-with-top-menu ' : '') +
					(appContentFullHeight ? 'app-content-full-height ' : '') +
					(hasScroll ? 'has-scroll ' : '')
				}
			>
				{renderComponents()}
			</div>
		</AppSettings.Provider>
	);
}

function App() {
	return (
		<RestaurantProvider>
			<AppContent />
		</RestaurantProvider>
	);
}

export default App;